/**
 * Task #1513 — Schemi Zod canonici per gli input di create/update sulle
 * 9 entità anagrafiche del POS.
 *
 * Sostituiscono il pattern legacy `data: any` nei service/route handler.
 * I tipi inferiti via `z.infer` sono i contratti tipizzati consumati da
 * `pos-warehouse.service.ts`, `pos-admin.service.ts` e dai relativi
 * wrapper (`pos-supplier.service.ts`, `pos-product-metadata.service.ts`).
 *
 * Convenzioni:
 *  - Create: campi anagrafici obbligatori richiesti (es. `name` per
 *    Supplier/Brand/Category/Carrier/Warehouse, `code+name` per Unit,
 *    `product_id+supplier_id` per ProductSupplierSku). I rimanenti sono
 *    opzionali/nullable per back-compat con i form esistenti.
 *  - Update: `.partial()` dello schema di create. Tutti i campi opzionali,
 *    semantica COALESCE già implementata lato SQL.
 *  - `.strip()` (default Zod) elimina silenziosamente eventuali campi extra
 *    inviati dal frontend → no break dei client esistenti.
 */
import { z } from 'zod';
import {
  isValidItalianVat,
  isValidItalianFiscalCode,
  isValidEmail,
  isValidSdiCode,
  isValidCap,
  isValidProvince,
  isValidUrl,
  isValidIban,
} from '../validators/italian-fiscal';
import { refineVatTriplet } from '../fiscal/vat-triplet';

const optStr = z.string().nullish();
const optBool = z.boolean().nullish();
const optNum = z.union([z.number(), z.string()]).nullish();
const optInt = z.union([z.number().int(), z.string()]).nullish();

const isBlankStr = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim().length === 0);

/**
 * Task #2617 — superRefine fiscale riusabile sugli schemi anagrafici.
 * Valida ogni campo SOLO se valorizzato (stringa non vuota): gli schemi di
 * update sono `.partial()` e il FE invia null/'' per i campi vuoti.
 * I validatori provengono dalla SoT condivisa `@easyflow/shared` → parità FE/BE.
 */
interface FiscalCheck {
  value: unknown;
  path: string;
  isValid: (v: string) => boolean;
  message: string;
}

function runFiscalChecks(ctx: z.RefinementCtx, checks: readonly FiscalCheck[]): void {
  for (const c of checks) {
    if (typeof c.value === 'string' && c.value.trim() !== '' && !c.isValid(c.value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [c.path], message: c.message });
    }
  }
}

// ==================== SUPPLIER ====================
/** Campi anagrafici condivisi supplier/carrier (stessi nomi colonna sulle spoke). */
interface AnagraficaFiscalFields {
  vat_number?: unknown;
  fiscal_code?: unknown;
  sdi_code?: unknown;
  email?: unknown;
  pec?: unknown;
  iban?: unknown;
  zip?: unknown;
  province?: unknown;
  website?: unknown;
}

/** superRefine condiviso per i campi anagrafici flat (supplier + carrier). */
function refineAnagraficaFlat(data: AnagraficaFiscalFields, ctx: z.RefinementCtx): void {
  runFiscalChecks(ctx, [
    { value: data.vat_number, path: 'vat_number', isValid: isValidItalianVat, message: 'Partita IVA non valida (checksum)' },
    { value: data.fiscal_code, path: 'fiscal_code', isValid: isValidItalianFiscalCode, message: 'Codice fiscale non valido' },
    { value: data.sdi_code, path: 'sdi_code', isValid: isValidSdiCode, message: 'Codice SDI non valido (6-7 alfanumerici)' },
    { value: data.email, path: 'email', isValid: isValidEmail, message: 'Email non valida' },
    { value: data.pec, path: 'pec', isValid: isValidEmail, message: 'PEC non valida' },
    { value: data.iban, path: 'iban', isValid: isValidIban, message: 'IBAN non valido (checksum)' },
    { value: data.zip, path: 'zip', isValid: isValidCap, message: 'CAP non valido (5 cifre)' },
    { value: data.province, path: 'province', isValid: isValidProvince, message: 'Sigla provincia non valida' },
    { value: data.website, path: 'website', isValid: isValidUrl, message: 'Sito web non valido (http/https)' },
  ]);
}

const PosSupplierBaseSchema = z.object({
  code: optStr,
  name: z.string().min(1, 'Nome fornitore obbligatorio'),
  trade_name: optStr,
  vat_number: optStr,
  fiscal_code: optStr,
  sdi_code: optStr,
  legal_form: optStr,
  address: optStr,
  city: optStr,
  province: optStr,
  zip: optStr,
  country: optStr,
  phone: optStr,
  email: optStr,
  pec: optStr,
  website: optStr,
  iban: optStr,
  bic: optStr,
  bank_name: optStr,
  payment_terms: optStr,
  notes: optStr,
  is_active: optBool,
});
export const PosSupplierCreateSchema = PosSupplierBaseSchema.superRefine(refineAnagraficaFlat);
export type PosSupplierCreateInput = z.infer<typeof PosSupplierBaseSchema>;
export const PosSupplierUpdateSchema = PosSupplierBaseSchema.partial().superRefine(refineAnagraficaFlat);
export type PosSupplierUpdateInput = z.infer<ReturnType<typeof PosSupplierBaseSchema.partial>>;

// ==================== CARRIER ====================
const PosCarrierBaseSchema = z.object({
  code: optStr,
  name: z.string().min(1, 'Nome vettore obbligatorio'),
  trade_name: optStr,
  vat_number: optStr,
  fiscal_code: optStr,
  sdi_code: optStr,
  legal_form: optStr,
  bic: optStr,
  address: optStr,
  city: optStr,
  province: optStr,
  zip: optStr,
  country: optStr,
  phone: optStr,
  mobile: optStr,
  email: optStr,
  pec: optStr,
  website: optStr,
  iban: optStr,
  bank_name: optStr,
  tracking_url_template: optStr,
  default_service_type: optStr,
  payment_terms: optStr,
  notes: optStr,
  is_active: optBool,
});
export const PosCarrierCreateSchema = PosCarrierBaseSchema.superRefine(refineAnagraficaFlat);
export type PosCarrierCreateInput = z.infer<typeof PosCarrierBaseSchema>;
export const PosCarrierUpdateSchema = PosCarrierBaseSchema.partial().superRefine(refineAnagraficaFlat);
export type PosCarrierUpdateInput = z.infer<ReturnType<typeof PosCarrierBaseSchema.partial>>;

// ==================== BRAND ====================
export const PosBrandCreateSchema = z.object({
  name: z.string().min(1, 'Nome brand obbligatorio'),
  logo_url: optStr,
  is_active: optBool,
});
export type PosBrandCreateInput = z.infer<typeof PosBrandCreateSchema>;
export const PosBrandUpdateSchema = PosBrandCreateSchema.partial();
export type PosBrandUpdateInput = z.infer<typeof PosBrandUpdateSchema>;

// ==================== RETAIL BRAND (Insegna — spoke hub `legal_entities`) ====================
// Task #2617 — `retail_brands` è la spoke "Insegne" con anagrafica fiscale
// completa (NON confondere con `pos_brands`, semplice name/logo). Coda role:
// `code` (derivato se assente) / `display_name` / `logo_url`.
const RetailBrandAddressSchema = z.object({
  street: optStr,
  city: optStr,
  province: optStr,
  zip: optStr,
  country: optStr,
});

const RetailBrandBaseSchema = z.object({
  // Coda role-specific.
  code: optStr,
  display_name: z.string().min(1, 'Nome insegna obbligatorio'),
  logo_url: optStr,
  // Anagrafica condivisa (hub-style: tax_code/pec_email/primary_*).
  legal_name: optStr,
  trade_name: optStr,
  vat_number: optStr,
  tax_code: optStr,
  sdi_code: optStr,
  legal_form: optStr,
  pec_email: optStr,
  registered_address: RetailBrandAddressSchema.nullish(),
  primary_iban: optStr,
  primary_bic: optStr,
  primary_phone: optStr,
  primary_email: optStr,
  website: optStr,
  is_active: optBool,
});

interface RetailBrandFiscalShape {
  vat_number?: unknown;
  tax_code?: unknown;
  sdi_code?: unknown;
  pec_email?: unknown;
  primary_email?: unknown;
  primary_iban?: unknown;
  website?: unknown;
  registered_address?: { zip?: unknown; province?: unknown } | null;
}

function refineRetailBrand(data: RetailBrandFiscalShape, ctx: z.RefinementCtx): void {
  runFiscalChecks(ctx, [
    { value: data.vat_number, path: 'vat_number', isValid: isValidItalianVat, message: 'Partita IVA non valida (checksum)' },
    { value: data.tax_code, path: 'tax_code', isValid: isValidItalianFiscalCode, message: 'Codice fiscale non valido' },
    { value: data.sdi_code, path: 'sdi_code', isValid: isValidSdiCode, message: 'Codice SDI non valido (6-7 alfanumerici)' },
    { value: data.pec_email, path: 'pec_email', isValid: isValidEmail, message: 'PEC non valida' },
    { value: data.primary_email, path: 'primary_email', isValid: isValidEmail, message: 'Email non valida' },
    { value: data.primary_iban, path: 'primary_iban', isValid: isValidIban, message: 'IBAN non valido (checksum)' },
    { value: data.website, path: 'website', isValid: isValidUrl, message: 'Sito web non valido (http/https)' },
  ]);
  const addr = data.registered_address;
  if (addr && typeof addr === 'object') {
    if (typeof addr.zip === 'string' && addr.zip.trim() !== '' && !isValidCap(addr.zip)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registered_address', 'zip'], message: 'CAP non valido (5 cifre)' });
    }
    if (typeof addr.province === 'string' && addr.province.trim() !== '' && !isValidProvince(addr.province)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registered_address', 'province'], message: 'Sigla provincia non valida' });
    }
  }
}

export const RetailBrandCreateSchema = RetailBrandBaseSchema.superRefine(refineRetailBrand);
export type RetailBrandCreateInput = z.infer<typeof RetailBrandBaseSchema>;
export const RetailBrandUpdateSchema = RetailBrandBaseSchema.partial().superRefine(refineRetailBrand);
export type RetailBrandUpdateInput = z.infer<ReturnType<typeof RetailBrandBaseSchema.partial>>;

// ==================== RETAIL BRAND CHANNEL (Canale insegna) ====================
// Task #2948 — `retail_brand_channels` è il canale di vendita per insegna
// (catalogo master-curato sulle insegne globali). `code` NOT NULL UNIQUE per
// `(brand_id, code)`: derivato dal display_name se assente lato service.
const RetailBrandChannelBaseSchema = z.object({
  code: optStr,
  display_name: z.string().min(1, 'Nome canale obbligatorio'),
  is_active: optBool,
  sort_order: optInt,
});
export const RetailBrandChannelCreateSchema = RetailBrandChannelBaseSchema;
export type RetailBrandChannelCreateInput = z.infer<typeof RetailBrandChannelBaseSchema>;
export const RetailBrandChannelUpdateSchema = RetailBrandChannelBaseSchema.partial();
export type RetailBrandChannelUpdateInput = z.infer<typeof RetailBrandChannelUpdateSchema>;

/**
 * Riga canale insegna come servita da `GET /retail-brands/:id/channels`
 * (canali attivi dell'insegna visibile al tenant): DTO condiviso per i
 * selettori insegna · canale del frontend (wizard gara, copia gara, ...).
 */
export const RetailBrandChannelOptionDtoSchema = z.object({
  id: z.string().uuid(),
  brand_id: z.string().uuid(),
  code: z.string(),
  display_name: z.string(),
  is_active: z.boolean(),
  sort_order: z.number().int(),
  is_locked: z.boolean(),
});
export type RetailBrandChannelOptionDto = z.infer<typeof RetailBrandChannelOptionDtoSchema>;

// ==================== BUSINESS DRIVER ====================
// Livello padre del catalogo, sopra le categorie (Driver -> Categorie -> Tipologie -> Prodotti).
export const PosBusinessDriverCreateSchema = z.object({
  name: z.string().min(1, 'Nome driver obbligatorio'),
  description: optStr,
  color: optStr,
  sort_order: optInt,
  is_active: optBool,
  // Scope store/insegna/canale: null = overall. Cascata Store → Insegna → Canale.
  // store_id richiede retail_brand_id e lo store deve appartenere all'insegna;
  // channel_id richiede retail_brand_id e — a store specifico — coincide con il
  // canale univoco della sede (store_brands.channel_id). Validato a service.
  retail_brand_id: optStr,
  store_id: optStr,
  channel_id: optStr,
});
export type PosBusinessDriverCreateInput = z.infer<typeof PosBusinessDriverCreateSchema>;
export const PosBusinessDriverUpdateSchema = PosBusinessDriverCreateSchema.partial();
export type PosBusinessDriverUpdateInput = z.infer<typeof PosBusinessDriverUpdateSchema>;

// ==================== CATEGORY ====================
export const PosCategoryCreateSchema = z.object({
  name: z.string().min(1, 'Nome categoria obbligatorio'),
  parent_id: optStr,
  business_driver_id: optStr,
  sort_order: optInt,
  is_active: optBool,
  // Task #3219 — ciclo di sostituzione atteso (mesi); null = mai obsoleto.
  replacement_cycle_months: z.number().int().positive().nullable().optional(),
});
export type PosCategoryCreateInput = z.infer<typeof PosCategoryCreateSchema>;
export const PosCategoryUpdateSchema = PosCategoryCreateSchema.partial();
export type PosCategoryUpdateInput = z.infer<typeof PosCategoryUpdateSchema>;

// ==================== UNIT OF MEASURE ====================
export const PosUnitCreateSchema = z.object({
  code: z.string().min(1, 'Codice unità obbligatorio'),
  name: z.string().min(1, 'Nome unità obbligatorio'),
});
export type PosUnitCreateInput = z.infer<typeof PosUnitCreateSchema>;
export const PosUnitUpdateSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
});
export type PosUnitUpdateInput = z.infer<typeof PosUnitUpdateSchema>;

// ==================== WAREHOUSE ====================
export const PosWarehouseStoreLinkSchema = z.object({
  store_id: z.string().min(1),
  is_reference: z.boolean().optional(),
});
export type PosWarehouseStoreLink = z.infer<typeof PosWarehouseStoreLinkSchema>;

export const PosWarehouseCreateSchema = z.object({
  code: optStr,
  name: z.string().min(1, 'Nome magazzino obbligatorio'),
  /** RS DIRETTA del magazzino (NOT NULL a DB): tutte le sedi servite devono appartenere a questa company. */
  company_id: z.string().uuid('Ragione sociale obbligatoria'),
  address: optStr,
  city: optStr,
  zip_code: optStr,
  is_default: optBool,
  is_active: optBool,
  /** Legacy: sede singola. La route layer lo normalizza in `stores[]`. */
  store_id: optStr,
  /** Array N:M (warehouse, store) — fonte canonica post-#1046. */
  stores: z.array(PosWarehouseStoreLinkSchema).optional(),
});
export type PosWarehouseCreateInput = z.infer<typeof PosWarehouseCreateSchema>;
export const PosWarehouseUpdateSchema = PosWarehouseCreateSchema.partial();
export type PosWarehouseUpdateInput = z.infer<typeof PosWarehouseUpdateSchema>;

// ==================== PRODUCT VARIANT ATTRIBUTES ====================
/**
 * Attributi variante (memoria/colore/condizione) letti dalle righe storiche
 * (`variant_attributes` jsonb). La scrittura delle varianti è disattivata:
 * questa shape serve solo alla propagazione in lettura (analytics, dettaglio
 * prodotto API).
 */
export const PosProductVariantAttributesSchema = z
  .object({
    memoria: z.string().trim().min(1).optional(),
    colore: z.string().trim().min(1).optional(),
    condizione: z.string().trim().min(1).optional(),
  })
  .catchall(z.string());
export type PosProductVariantAttributes = z.infer<typeof PosProductVariantAttributesSchema>;

// ==================== PRODUCT-SUPPLIER SKU ====================
/**
 * Mapping anagrafico canonico tra un prodotto interno e il codice usato da un
 * fornitore. `product_id` arriva esclusivamente dal parametro di percorso
 * (`/products/:id/supplier-skus`): non è accettato nel body per evitare che un
 * client possa scrivere il mapping su un prodotto diverso da quello mostrato.
 */
export const PosProductSupplierSkuInputSchema = z.object({
  variant_id: z.string().uuid().nullable().optional(),
  supplier_id: z.string().uuid('supplier_id deve essere un UUID valido'),
  supplier_sku: z.string().trim().min(1, 'supplier_sku obbligatorio').max(120, 'supplier_sku troppo lungo'),
}).strict();
export type PosProductSupplierSkuInput = z.infer<typeof PosProductSupplierSkuInputSchema>;

/**
 * Compare-and-swap input used by the reusable supplier-SKU picker.  The
 * expected mapping is deliberately required: `null` means the caller
 * observed no mapping, while an object identifies the exact mapping version
 * it observed.  This prevents a stale picker from silently overwriting a
 * concurrent edit.
 */
export const PosProductSupplierSkuAssociationInputSchema = PosProductSupplierSkuInputSchema.extend({
  expected_mapping: z.union([
    z.null(),
    z.object({
      id: z.string().uuid(),
      supplier_sku: z.string().trim().min(1).max(120),
      // Optional only for backwards compatibility with the first CAS
      // contract. New clients send the raw six-digit ISO version returned by
      // the API; it must never be converted through JavaScript Date.
      updated_at: z.string().datetime({ offset: true }).optional(),
    }).strict(),
  ]),
}).strict();
export type PosProductSupplierSkuAssociationInput = z.infer<typeof PosProductSupplierSkuAssociationInputSchema>;

/**
 * Query usata dal lookup batch di ricevimento. Il lookup resta separato dal
 * CRUD per prodotto perché riceve più product_id e restituisce una mappa.
 */
export const PosSupplierSkusQuerySchema = z.object({
  supplier_id: z.string().uuid(),
  product_ids: z.string().min(1)
    .transform((value) => value.split(',').map((part) => part.trim()).filter(Boolean))
    .pipe(z.array(z.string().uuid()).min(1).max(500)),
}).strict();

// ==================== PRODUCT (canonico post-#1475) ====================
const SerialModeSchema = z.enum(['none', 'unique', 'non_unique']);
/** Natura anagrafica del prodotto (pos_products.product_nature). Task #4332: condivisa con i fatti gare. */
export const PRODUCT_NATURES = ['product', 'service', 'canvas'] as const;
export type ProductNature = (typeof PRODUCT_NATURES)[number];
export const PRODUCT_NATURE_LABELS: Record<ProductNature, string> = {
  product: 'Prodotto',
  service: 'Servizio',
  canvas: 'Canvass',
};
const ProductNatureSchema = z.enum(PRODUCT_NATURES);
// Task #3005 — Strategia Prelievo (comune) e Clienti Target Canvas (allineato a
// `lead_type` del CRM: b2c/b2b; NULL = entrambi).
export const PickingStrategySchema = z.enum(['fifo', 'lifo', 'fefo']);

/** Stato anagrafico articolo (`pos_products.product_status`). */
export const ProductStatusSchema = z.enum(['active', 'inactive', 'discontinued', 'draft']);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;
export type PickingStrategy = z.infer<typeof PickingStrategySchema>;
export type ProductSerialMode = z.infer<typeof SerialModeSchema>;

/**
 * Strategia di prelievo (FIFO/LIFO/FEFO) e tracciamento articolo.
 *
 * La strategia decide quale LOTTO esce per primo: ha senso solo per gli
 * articoli tracciati a lotto (`serial_mode='non_unique'`). Per un articolo
 * generico esiste un solo bucket per magazzino, per un serializzabile il
 * prelievo è per seriale, e FEFO richiede la scadenza a ogni carico, che
 * esiste solo con i lotti. Regola condivisa FE/BE: una strategia diversa dal
 * default FIFO è ammessa SOLO con tracciamento a lotto; per ogni altro
 * tracciamento il valore persistito resta FIFO.
 */
export const DEFAULT_PICKING_STRATEGY: PickingStrategy = 'fifo';
export const PICKING_STRATEGY_LOT_ONLY_MESSAGE =
  'La strategia di prelievo LIFO/FEFO è ammessa solo per articoli tracciati a lotto.';

export function isPickingStrategyAllowedForSerialMode(
  serialMode: ProductSerialMode | null | undefined,
  pickingStrategy: PickingStrategy | null | undefined,
): boolean {
  if (!pickingStrategy || pickingStrategy === DEFAULT_PICKING_STRATEGY) return true;
  return serialMode === 'non_unique';
}

/** Strategia effettiva da persistere: fuori dal tracciamento a lotto torna sempre al default. */
export function resolvePickingStrategyForSerialMode(
  serialMode: ProductSerialMode | null | undefined,
  pickingStrategy: PickingStrategy | null | undefined,
): PickingStrategy {
  if (serialMode !== 'non_unique') return DEFAULT_PICKING_STRATEGY;
  return pickingStrategy ?? DEFAULT_PICKING_STRATEGY;
}
export const TargetCustomersSchema = z.enum(['b2c', 'b2b']);
export type TargetCustomers = z.infer<typeof TargetCustomersSchema>;

/**
 * Task #3005 — Shape base del prodotto. La validazione per-natura (Canvas vs
 * Fisico/Servizio) è applicata via `superRefine` sui derivati Create/Update.
 * `sku`/`ean` sono `optStr` nella shape base ma OBBLIGATORI via
 * `requireProductIdentity` (superRefine) e NOT NULL a schema DB (Task #3082).
 * I campi Canvas (`monthly_fee`/`retail_brand_id`/
 * `channel_ids`/`target_customers`) sono ammessi SOLO per natura `canvas`.
 * Identità Canvas: insegna obbligatoria, uno o più canali dell'insegna, target
 * unico B2C|B2B, SKU obbligatorio, EAN facoltativo, mai scorte né seriali.
 */
export const posProductRecognitionDecisionSchema = z.object({
  review_id: z.string().uuid(),
  draft_hash: z.string(),
  difference: z.string().trim().min(3).max(500),
});

const PosProductBaseSchema = z.object({
  creation_operation_id: z.string().uuid().optional(),
  recognition_decision: posProductRecognitionDecisionSchema.optional(),
  sku: optStr,
  ean: optStr,
  /**
   * EAN ulteriori della medesima anagrafica. `ean` resta il codice principale
   * compatibile con le superfici storiche; gli alternativi sono persistiti
   * normalizzati, mai serializzati nel campo principale.
   */
  alternate_eans: z.array(
    z.string().trim().min(1, 'EAN alternativo non valido').max(50, 'EAN alternativo troppo lungo'),
  ).max(100, 'Massimo 100 EAN alternativi').optional(),
  name: z.string().min(1, 'Nome prodotto obbligatorio'),
  description: optStr,
  variant_info: optStr,
  brand_id: optStr,
  category_id: optStr,
  unit_of_measure_id: optStr,
  supplier_id: optStr,
  product_type_id: optStr,
  /**
   * Terna fiscale dell'articolo, stesso modello di ordine fornitore e DDT:
   * `vat_rate` = aliquota numerica (22, 10, 5, 4, 0); Natura IVA e
   * Riferimento Normativo esistono SOLO a 0% e allora sono obbligatori
   * (regola unica shared `vat-triplet`). Il trattamento `pos_vat_rates`
   * (`vat_rate_id`) è risolto dal service in modo deterministico dalla terna.
   * `vat_rate_id` resta accettato come input legacy/API: se presente insieme
   * alla terna, il service ne verifica la coerenza (aliquota e natura).
   * Codici natura = enum DB `pos_vat_nature` (catalogo globale `natura_iva_map`).
   */
  vat_rate: z.number().finite().min(0).max(100).nullish(),
  vat_rate_id: optStr,
  vat_nature: optStr,
  riferimento_normativo: optStr,
  sell_price: optNum,
  serial_mode: SerialModeSchema.optional(),
  // Opzione-2 — il tipo di codice univoco (IMEI/ICCID/MAC/Seriale) è dichiarato
  // a livello ARTICOLO. Obbligatorio quando serial_mode='unique', ignorato
  // altrimenti (persistito come serial_kinds_required=[{kind,count:1}]).
  serial_kind: z.enum(['imei', 'iccid', 'macaddr', 'serial']).optional(),
  product_nature: ProductNatureSchema.optional(),
  track_inventory: optBool,
  product_status: ProductStatusSchema.optional(),
  // Task #3005 — Comuni a tutte le nature.
  valid_from: optStr,
  valid_until: optStr,
  /**
   * Strategia di prelievo: LIFO/FEFO ammesse solo con `serial_mode='non_unique'`
   * (vedi `isPickingStrategyAllowedForSerialMode`). Fuori dal tracciamento a
   * lotto il backend persiste sempre il default FIFO.
   */
  picking_strategy: PickingStrategySchema.optional(),
  notes: optStr,
  // Task #3005 — Canvas-only.
  monthly_fee: optNum,
  retail_brand_id: optStr,
  /** Canali dell'insegna coperti dall'articolo Canvas (junction pos_product_channels, sostituzione integrale). */
  channel_ids: z.array(z.string().uuid()).optional(),
  target_customers: TargetCustomersSchema.optional(),
});

type PosProductShape = z.infer<typeof PosProductBaseSchema>;
const CANVAS_ONLY_FIELDS = ['monthly_fee', 'retail_brand_id', 'channel_ids', 'target_customers'] as const;

/** Un campo Canvas conta come "valorizzato" se non è vuoto (array vuoto = assente). */
function hasCanvasValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Validazione condizionale per-natura (Task #3005):
 *  - canvas: canone, insegna, ≥1 canale e target obbligatori, niente seriali né scorte;
 *  - product/service: i campi Canvas non sono ammessi.
 * In update la natura può essere assente (non modificata): in quel caso si
 * applica solo la coerenza canali→insegna (il resto è verificato dal service
 * contro la natura corrente).
 */
function refineProductByNature(data: Partial<PosProductShape>, ctx: z.RefinementCtx): void {
  if (hasCanvasValue(data.channel_ids) && data.retail_brand_id === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['channel_ids'], message: "I Canali richiedono un'Insegna" });
  }
  // Terna fiscale (regola unica shared): con `vat_rate` dichiarata, natura e
  // riferimento obbligatori a 0% e vietati altrimenti; senza `vat_rate`
  // (input legacy via `vat_rate_id`, o update parziale) la natura dichiarata
  // deve comunque portare il riferimento; il resto è verificato dal service
  // contro l'aliquota effettiva.
  refineVatTriplet(data, ctx, { zeroRate: 'nature_and_reference' });
  const nature = data.product_nature;
  if (nature === 'canvas') {
    if (data.monthly_fee === undefined || data.monthly_fee === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['monthly_fee'], message: 'Canone Mensile obbligatorio per Canvas' });
    }
    if (isBlankStr(data.retail_brand_id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['retail_brand_id'], message: 'Insegna obbligatoria per Canvas' });
    }
    if (!hasCanvasValue(data.channel_ids)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['channel_ids'], message: "Almeno un canale dell'insegna obbligatorio per Canvas" });
    }
    if (!data.target_customers) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['target_customers'], message: 'Target (B2C o B2B) obbligatorio per Canvas' });
    }
    if (data.serial_mode && data.serial_mode !== 'none') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serial_mode'], message: 'Canvas non ammette seriali' });
    }
    if (data.track_inventory === true) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['track_inventory'], message: 'Canvas non gestisce scorte' });
    }
  } else if (nature === 'product' || nature === 'service') {
    for (const field of CANVAS_ONLY_FIELDS) {
      if (hasCanvasValue(data[field])) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Campo ammesso solo per natura Canvas' });
      }
    }
  }
  // Opzione-2 — se l'articolo è serializzato (unique) il tipo identificativo è
  // dichiarato a livello articolo ed è obbligatorio.
  if (data.serial_mode === 'unique' && !data.serial_kind) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serial_kind'], message: 'Tipo identificativo obbligatorio per articoli serializzati' });
  }
  // Strategia di prelievo diversa da FIFO solo con tracciamento a lotto. In
  // update `serial_mode` può mancare (non modificato): il confronto con il
  // tracciamento corrente è applicato lato service.
  if (data.serial_mode !== undefined && !isPickingStrategyAllowedForSerialMode(data.serial_mode, data.picking_strategy)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['picking_strategy'], message: PICKING_STRATEGY_LOT_ONLY_MESSAGE });
  }
}

/**
 * Convenzione identità prodotto: SKU obbligatorio per OGNI natura; EAN/Barcode
 * obbligatorio per prodotti e servizi (identità scansionabile in magazzino) e
 * facoltativo per Canvas (canone senza scorte). In update con natura assente
 * l'obbligo EAN è verificato dal service contro la natura corrente.
 */
function requireProductIdentity(data: Partial<PosProductShape>, ctx: z.RefinementCtx): void {
  if (isBlankStr(data.sku)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sku'], message: 'SKU obbligatorio' });
  }
  if (isBlankStr(data.ean) && (data.product_nature === 'product' || data.product_nature === 'service')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ean'], message: 'EAN/Barcode obbligatorio' });
  }
  if (data.alternate_eans) {
    const identities = new Set<string>();
    const addIdentity = (value: string, path: ['ean'] | ['alternate_eans', number]) => {
      const normalized = value.trim().toLocaleLowerCase();
      if (identities.has(normalized)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'Ogni EAN può comparire una sola volta nella stessa anagrafica',
        });
      }
      identities.add(normalized);
    };
    if (typeof data.ean === 'string' && !isBlankStr(data.ean)) {
      addIdentity(data.ean, ['ean']);
    }
    data.alternate_eans.forEach((ean, index) => addIdentity(ean, ['alternate_eans', index]));
  }
}

export const PosProductCreateSchema = PosProductBaseSchema.superRefine(refineProductByNature).superRefine(requireProductIdentity);
export type PosProductCreateInput = z.infer<typeof PosProductCreateSchema>;
export const PosProductUpdateSchema = PosProductBaseSchema.partial().superRefine(refineProductByNature).superRefine(requireProductIdentity);
export type PosProductUpdateInput = z.infer<typeof PosProductUpdateSchema>;

// ==================== Anagrafica row types riusati dal frontend ====================
/** Forma minima riusata da `CatalogProducts.tsx` per dropdown anagrafici. */
export interface PosNamedRow {
  id: string;
  name: string;
  is_active?: boolean | null;
}
export interface PosUnitRow {
  id: string;
  code: string;
  name: string;
}
